/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CanonicalToolAttributes,
  CanonicalToolAttributes$inboundSchema,
} from "./canonicaltoolattributes.js";
import {
  ToolAnnotations,
  ToolAnnotations$inboundSchema,
} from "./toolannotations.js";
import { ToolVariation, ToolVariation$inboundSchema } from "./toolvariation.js";

/**
 * A platform-owned tool served directly by the platform
 */
export type PlatformToolDefinition = {
  /**
   * Tool annotations providing behavioral hints about the tool
   */
  annotations?: ToolAnnotations | undefined;
  /**
   * The original details of a tool
   */
  canonical?: CanonicalToolAttributes | undefined;
  /**
   * The canonical name of the tool. Will be the same as the name if there is no variation.
   */
  canonicalName: string;
  /**
   * Confirmation mode for the tool
   */
  confirm?: string | undefined;
  /**
   * Prompt for the confirmation
   */
  confirmPrompt?: string | undefined;
  /**
   * The creation date of the tool.
   */
  createdAt: Date;
  /**
   * Description of the tool
   */
  description: string;
  /**
   * The ID of the tool
   */
  id: string;
  /**
   * The name of the tool
   */
  name: string;
  /**
   * Optional owning entity ID
   */
  ownerId?: string | undefined;
  /**
   * The entity kind that owns this tool's lifecycle
   */
  ownerKind?: string | undefined;
  /**
   * The ID of the project
   */
  projectId: string;
  /**
   * JSON schema for the request
   */
  schema: string;
  /**
   * Version of the schema
   */
  schemaVersion?: string | undefined;
  /**
   * The backing platform tool source (for example: logs)
   */
  sourceSlug: string;
  /**
   * Summarizer for the tool
   */
  summarizer?: string | undefined;
  /**
   * The URN of this tool
   */
  toolUrn: string;
  /**
   * The last update date of the tool.
   */
  updatedAt: Date;
  variation?: ToolVariation | undefined;
};

/** @internal */
export const PlatformToolDefinition$inboundSchema: z.ZodMiniType<
  PlatformToolDefinition,
  unknown
> = z.pipe(
  z.object({
    annotations: z.optional(ToolAnnotations$inboundSchema),
    canonical: z.optional(CanonicalToolAttributes$inboundSchema),
    canonical_name: z.string(),
    confirm: z.optional(z.string()),
    confirm_prompt: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    description: z.string(),
    id: z.string(),
    name: z.string(),
    owner_id: z.optional(z.string()),
    owner_kind: z.optional(z.string()),
    project_id: z.string(),
    schema: z.string(),
    schema_version: z.optional(z.string()),
    source_slug: z.string(),
    summarizer: z.optional(z.string()),
    tool_urn: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    variation: z.optional(ToolVariation$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "canonical_name": "canonicalName",
      "confirm_prompt": "confirmPrompt",
      "created_at": "createdAt",
      "owner_id": "ownerId",
      "owner_kind": "ownerKind",
      "project_id": "projectId",
      "schema_version": "schemaVersion",
      "source_slug": "sourceSlug",
      "tool_urn": "toolUrn",
      "updated_at": "updatedAt",
    });
  }),
);

export function platformToolDefinitionFromJSON(
  jsonString: string,
): SafeParseResult<PlatformToolDefinition, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => PlatformToolDefinition$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'PlatformToolDefinition' from JSON`,
  );
}
